import { memo, useEffect, useMemo, useRef, type CSSProperties, type MouseEvent } from 'react';

import { SpeakerBadge } from '@/components/SpeakerBadge/SpeakerBadge';
import {
  formatClock,
  speakerKey,
  wordAt,
  wordsOf,
  type Speaker,
  type TranscriptSegment,
  type WordMark,
} from '@/papermill/transcript';

import styles from './TranscriptView.module.css';

export interface TranscriptViewProps {
  segments: TranscriptSegment[];
  speakers: Speaker[];
  /** Where playback is; drives the highlighted word. */
  currentMs: number;
  /** Keep the turn being spoken in view. */
  follow?: boolean;
  /** Extracted values by `segmentId:wordIndex`. */
  marks?: Map<string, WordMark[]>;
  onSeek: (ms: number) => void;
  /** Opens a value heard in a turn. */
  onOpenValue?: (entityId: string, field: string) => void;
}

type TurnState = 'past' | 'active' | 'future';

interface TurnProps {
  segment: TranscriptSegment;
  speaker: Speaker | undefined;
  state: TurnState;
  activeWord: number;
  marks: Map<string, WordMark[]> | undefined;
  onSeek: (ms: number) => void;
  onOpenValue?: (entityId: string, field: string) => void;
}

const Turn = memo(function Turn({
  segment,
  speaker,
  state,
  activeWord,
  marks,
  onSeek,
  onOpenValue,
}: TurnProps) {
  const words = useMemo(() => wordsOf(segment), [segment]);
  const values = useMemo(() => {
    const seen = new Map<string, WordMark>();
    words.forEach((_, index) => {
      for (const mark of marks?.get(`${segment.id}:${index}`) ?? []) {
        seen.set(`${mark.entityId}:${mark.field}`, mark);
      }
    });
    return [...seen.values()];
  }, [marks, segment.id, words]);

  function seekToWord(event: MouseEvent<HTMLParagraphElement>) {
    const index = (event.target as HTMLElement).dataset.word;
    if (index !== undefined) {
      onSeek(words[Number(index)].start_ms);
    }
  }

  return (
    <li
      className={`${styles.turn} ${styles[state]}`}
      data-active={state === 'active' || undefined}
      style={{ '--speaker': `var(--series-${speaker?.slot ?? 1})` } as CSSProperties}
    >
      <div className={styles.turnHeader}>
        {speaker ? <SpeakerBadge speaker={speaker} size="sm" /> : null}
        <button
          type="button"
          className={styles.time}
          onClick={() => onSeek(segment.start_ms)}
          aria-label={`Play from ${formatClock(segment.start_ms)}`}
        >
          {formatClock(segment.start_ms)}
        </button>
      </div>
      <p className={styles.text} onClick={seekToWord}>
        {words.map((word, index) => {
          const wordMarks = marks?.get(`${segment.id}:${index}`);
          const spoken = state === 'past' || (state === 'active' && index < activeWord);
          return (
            <span key={index}>
              <span
                data-word={index}
                className={[
                  styles.word,
                  spoken ? styles.spoken : '',
                  state === 'active' && index === activeWord ? styles.current : '',
                  wordMarks ? styles.marked : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={
                  wordMarks
                    ? ({ '--mark': `var(--series-${wordMarks[0].category})` } as CSSProperties)
                    : undefined
                }
                title={wordMarks?.map((mark) => mark.label).join('\n')}
              >
                {word.text}
              </span>{' '}
            </span>
          );
        })}
      </p>
      {values.length > 0 && onOpenValue ? (
        <ul className={styles.values} aria-label="Values heard">
          {values.map((mark) => (
            <li key={`${mark.entityId}:${mark.field}`}>
              <button
                type="button"
                className={styles.value}
                style={{ '--mark': `var(--series-${mark.category})` } as CSSProperties}
                onClick={() => onOpenValue(mark.entityId, mark.field)}
              >
                <span className={styles.valueDot} aria-hidden="true" />
                {mark.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
});

/** The conversation, turn by turn: words light up as they are spoken; tap one to jump there. */
export function TranscriptView({
  segments,
  speakers,
  currentMs,
  follow = false,
  marks,
  onSeek,
  onOpenValue,
}: TranscriptViewProps) {
  const listRef = useRef<HTMLOListElement>(null);
  const ordered = useMemo(() => [...segments].sort((a, b) => a.start_ms - b.start_ms), [segments]);
  const bySpeaker = useMemo(() => new Map(speakers.map((s) => [s.key, s])), [speakers]);
  const active = wordAt(ordered, currentMs);
  const activeSegment = active >= 0 && currentMs <= ordered[active].end_ms + 600 ? active : -1;
  const activeWords = activeSegment >= 0 ? wordsOf(ordered[activeSegment]) : [];
  const activeWord = activeSegment >= 0 ? wordAt(activeWords, currentMs) : -1;

  useEffect(() => {
    const list = listRef.current;
    if (!follow || !list || activeSegment < 0) {
      return;
    }
    const item = list.children[activeSegment] as HTMLElement | undefined;
    if (!item) {
      return;
    }
    const top = item.offsetTop - list.offsetTop;
    if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTo({ top: Math.max(0, top - list.clientHeight / 3), behavior: 'smooth' });
    }
  }, [activeSegment, follow]);

  if (ordered.length === 0) {
    return <p className={styles.empty}>Nothing was said in this recording.</p>;
  }

  return (
    <ol ref={listRef} className={styles.list} aria-label="Transcript">
      {ordered.map((segment, index) => (
        <Turn
          key={segment.id}
          segment={segment}
          speaker={bySpeaker.get(speakerKey(segment.track_id, segment.speaker))}
          state={index === activeSegment ? 'active' : index <= active ? 'past' : 'future'}
          activeWord={index === activeSegment ? activeWord : -1}
          marks={marks}
          onSeek={onSeek}
          onOpenValue={onOpenValue}
        />
      ))}
    </ol>
  );
}
